import {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'

import RecordingDecision from './RecordingDecision'
import {ReadModeSwitch, ReadingBody, RecordControls, readingHint} from './RecordVersionPanel'
import {CloseIcon} from './icons'
import {RECORD_VERSION_COPY} from './hooks/useRecordVersion'
import useScrollLock from './hooks/useScrollLock'

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * "Record this version" on desktop (TK-3204): the story in large type, word
 * for word or as talking points, with Start/Stop and Pause beneath it. With
 * a recording already on the story the modal turns into "Use your new
 * recording?" once the take lands, with both to listen to.
 *
 * Clicking the scrim or pressing Escape closes it, but not while a take is
 * in progress; closing on the decision parks the take as a draft (the hook
 * handles both). Focus is trapped inside and returned on close.
 */
const RecordVersionModal = ({recordVersion, text, currentRecording}) => {
  const {mode, deciding, take, recorder} = recordVersion
  const dialogRef = useRef(null)
  const speakRef = useRef(null)
  const useRef_ = useRef(null)
  const scrimRef = useRef(null)

  // The story behind must not scroll under the modal (body and the flow's own scroll container).
  useScrollLock(scrimRef)

  useEffect(() => {
    if (!deciding && speakRef.current) speakRef.current.focus()
  }, [deciding])

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        recordVersion.close()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [recordVersion.close])

  const handleKeyDown = event => {
    if (event.key !== 'Tab' || !dialogRef.current) return

    const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE))

    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const handleScrimPointerDown = event => {
    if (event.target === event.currentTarget) recordVersion.close()
  }

  const showDecision = deciding && take && currentRecording

  return (
    <div className='ls-rv-scrim' ref={scrimRef} onPointerDown={handleScrimPointerDown}>
      <div aria-labelledby='ls-rv-title' aria-modal='true' className='ls-rv' ref={dialogRef} role='dialog' onKeyDown={handleKeyDown}>
        <div className='ls-rv-head'>
          <div>
            <h2 id='ls-rv-title'>{showDecision ? RECORD_VERSION_COPY.decideTitle : RECORD_VERSION_COPY.title}</h2>
            <p>
              {
                showDecision ?
                  RECORD_VERSION_COPY.decideSub :
                  `${RECORD_VERSION_COPY.sub}${currentRecording ? RECORD_VERSION_COPY.subWithCurrent : ''}`
              }
            </p>
          </div>
          <button aria-label='Close' className='ls-icon-btn ls-mini-btn ls-rv-close' disabled={recorder.locked} type='button' onClick={recordVersion.close}>
            <CloseIcon size={16} />
          </button>
        </div>
        {
          !showDecision &&
            <div className='ls-rv-tabs'>
              <ReadModeSwitch disabled={recorder.busy} mode={mode} onChange={recordVersion.chooseMode} />
            </div>
        }
        <div className='ls-rv-body'>
          {
            showDecision ?
              <RecordingDecision current={currentRecording} take={take} useRef={useRef_} onKeep={recordVersion.keepCurrent} onUse={recordVersion.useNew} /> :
              <ReadingBody mode={mode} points={recordVersion.points} pointsLoading={recordVersion.pointsLoading} text={text} />
          }
        </div>
        {
          !showDecision &&
            <div className='ls-rv-foot'>
              <RecordControls className='ls-speak-btn' recorder={recorder} speakRef={speakRef} />
              <span className='ls-rv-hint'>{readingHint({mode, state: recorder.state})}</span>
            </div>
        }
      </div>
    </div>
  )
}

RecordVersionModal.propTypes = {
  currentRecording: PropTypes.object,
  recordVersion: PropTypes.shape({
    chooseMode: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    deciding: PropTypes.bool.isRequired,
    keepCurrent: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(PropTypes.string),
    pointsLoading: PropTypes.bool,
    recorder: PropTypes.object.isRequired,
    take: PropTypes.object,
    useNew: PropTypes.func.isRequired
  }).isRequired,
  text: PropTypes.string.isRequired
}

export default RecordVersionModal
